import React, { useMemo, useState, ChangeEvent, CompositionEvent } from "react";
import styled from "styled-components";

// GuideImage / TitleBlock は .jsx のため TS の型宣言ファイルが無く、暗黙の any
// 警告が出るので個別に抑制する（プロジェクト全体で .tsx 化が進むまでの暫定対応）。
// @ts-ignore
import GuideImage from "../../../components/guide-image/GuideImage";
// @ts-ignore
import TitleBlock from "../../../components/block/TitleBlock";
import Input from "../../../components/form-item/Input";
import FilterChip from "../../../components/filter-chip/FilterChip";

/**
 * 旧 BE の artist_create_product.js から移植した目的物カテゴリ。
 * 親タグ（ジャンル）→ 子タグ（プラットフォーム）の二段構成で、
 * クリックすると目的物テキストが「(子) + (suffix) + (親) + 「タイトル（仮）」」
 * のフォーマットで組み立てられる。
 */
interface ChildTag {
  name: string;
  suffix?: string;
}

interface ParentTag {
  name: string;
  children: ChildTag[];
}

const TARGET_OBJECTS: ParentTag[] = [
  {
    name: "音楽",
    children: [
      { name: "ストリーミング配信", suffix: "向け" },
      { name: "アルバム", suffix: "向け" },
      { name: "CM" },
      { name: "PV", suffix: "向け" },
      { name: "遊技機", suffix: "向け" },
      { name: "舞台" },
      { name: "ゲーム" },
      { name: "映画" },
    ],
  },
  {
    name: "公演",
    children: [
      { name: "オンライン" },
      { name: "朗読劇" },
      { name: "舞台" },
      { name: "劇場" },
      { name: "ライブハウス" },
      { name: "コンサート" },
    ],
  },
  {
    name: "アート",
    children: [
      { name: "ビジュアル" },
      { name: "メディア" },
      { name: "テキスタイル" },
      { name: "パフォーマンス" },
      { name: "インスタレーション" },
      { name: "コンテンポラリー" },
    ],
  },
  {
    name: "CM",
    children: [
      { name: "YouTube", suffix: "向け" },
      { name: "地上波テレビ", suffix: "向け" },
      { name: "ケーブルテレビ", suffix: "向け" },
      { name: "衛星テレビ", suffix: "向け" },
      { name: "インターネットラジオ" },
      { name: "AM/FMラジオ", suffix: "向け" },
      { name: "ポッドキャスト" },
      { name: "SNS広告", suffix: "向け" },
      { name: "バナー" },
      { name: "アプリ内" },
      { name: "ビルボード", suffix: "向け" },
      { name: "交通広告", suffix: "向け" },
    ],
  },
  {
    name: "PV",
    children: [
      { name: "YouTube", suffix: "向け" },
      { name: "SNS", suffix: "向け" },
      { name: "オンライン広告", suffix: "向け" },
      { name: "Webサイト", suffix: "向け" },
    ],
  },
  {
    name: "MV",
    children: [
      { name: "YouTube", suffix: "向け" },
      { name: "SNS", suffix: "向け" },
      { name: "オンライン広告", suffix: "向け" },
      { name: "Webサイト", suffix: "向け" },
    ],
  },
  {
    name: "番組",
    children: [
      { name: "トーク" },
      { name: "ドキュメンタリー" },
      { name: "教育" },
      { name: "料理" },
      { name: "スポーツ" },
      { name: "旅" },
    ],
  },
  {
    name: "ゲーム",
    children: [
      { name: "PlayStation", suffix: "向け" },
      { name: "Xbox Series", suffix: "向け" },
      { name: "Nintendo Switch", suffix: "向け" },
      { name: "Steam", suffix: "向け" },
      { name: "Mobile", suffix: "向け" },
    ],
  },
  {
    name: "遊技機",
    children: [
      { name: "ぱちんこ" },
      { name: "パチスロ" },
    ],
  },
  {
    name: "映画",
    children: [
      { name: "YouTube", suffix: "向け" },
      { name: "AbemaTV", suffix: "向け" },
      { name: "Netflix", suffix: "向け" },
      { name: "Amazon Prime Video", suffix: "向け" },
      { name: "Disney+", suffix: "向け" },
      { name: "Hulu", suffix: "向け" },
      { name: "AppleTV+", suffix: "向け" },
    ],
  },
  {
    name: "ドラマシリーズ",
    children: [
      { name: "YouTube", suffix: "向け" },
      { name: "AbemaTV", suffix: "向け" },
      { name: "Netflix", suffix: "向け" },
      { name: "Amazon Prime Video", suffix: "向け" },
      { name: "Disney+", suffix: "向け" },
      { name: "Hulu", suffix: "向け" },
      { name: "AppleTV+", suffix: "向け" },
    ],
  },
];

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`;

/**
 * 親タグクリック時の目的物テキスト更新ロジック。
 * 既存テキストの「『「』より前の部分」を親タグ名で置換する。
 * 旧 BE: replace(/^(.*?)(「.*)/, '$2') → `「` 以降のみ残す。
 */
const applyParentTag = (current: string, parentName: string): string => {
  const match = current.match(/^(.*?)(「.*)$/);
  if (match) {
    return parentName + match[2];
  }
  return parentName + current;
};

/**
 * 子タグクリック時の目的物テキスト更新ロジック。
 * 既存テキストから「先頭〜（最初に現れる親タグ名の直前）」までを削り、
 * その手前に「子タグ + suffix」を付ける。
 *
 * 親タグ名は `TARGET_OBJECTS` 由来の固定文字列（漢字 / カタカナ）想定で、
 * 正規表現メタ文字を含まない前提のためエスケープは行わない。
 */
const applyChildTag = (
  current: string,
  childName: string,
  suffix: string | undefined,
): string => {
  let trimmed = current;
  for (const parent of TARGET_OBJECTS) {
    trimmed = trimmed.replace(new RegExp("^(.*?)(?=" + parent.name + ")"), "");
  }
  return childName + (suffix ?? "") + trimmed;
};

interface ProjectObjectProps {
  value: string;
  // 関数型シグネチャの引数名は形式的なドキュメント目的のため eslint の
  // unused-vars チェックの対象外にする（同名 prop の `value` と紛らわしいが、
  // 既存 onChange パターンとの一貫性のために `value` 表記を維持する）。
  // eslint-disable-next-line no-unused-vars
  onChange: (value: string) => void;
}

/**
 * プロジェクト作成ウィザード用の目的物入力。
 * 旧 BE の artist_create_product 画面にあった「ジャンル / プラットフォームを
 * クリックすると目的物テキストが組み立てられる」UI を React で移植した。
 */
const ProjectObject: React.FC<ProjectObjectProps> = ({ value, onChange }) => {
  const [selectedParent, setSelectedParent] = useState<string | null>(null);

  const childTags = useMemo(() => {
    if (!selectedParent) return [];
    return TARGET_OBJECTS.find((p) => p.name === selectedParent)?.children ?? [];
  }, [selectedParent]);

  const handleParentClick = (parentName: string) => {
    setSelectedParent(parentName);
    onChange(applyParentTag(value, parentName));
  };

  const handleChildClick = (child: ChildTag) => {
    onChange(applyChildTag(value, child.name, child.suffix));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  // IME 確定時、ブラウザ実装によっては `change` より先に `compositionend` が
  // 発火するため、確定後の値を確実に親へ伝えるために onCompositionEnd でも
  // onChange を呼ぶ。
  const handleCompositionEnd = (e: CompositionEvent<HTMLInputElement>) => {
    onChange((e.target as HTMLInputElement).value);
  };

  return (
    <div className="u-col u-w100 u-gap16">
      <TitleBlock
        title="目的物"
        description="作品の形式やジャンル、対象プラットフォームをタグから選ぶと、目的物のテキストが自動で組み立てられます。あとから自由に編集できます。"
      />
      <GuideImage name="project_object" />

      <div className="u-col u-w100 u-gap8">
        <span className="bodytext-13 u-text-light-gray u-pl4">
          作品の形式またはジャンル
        </span>
        <TagRow>
          {TARGET_OBJECTS.map((parent) => (
            <FilterChip
              key={parent.name}
              size="medium"
              selected={selectedParent === parent.name}
              onClick={() => handleParentClick(parent.name)}
            >
              {parent.name}
            </FilterChip>
          ))}
        </TagRow>
      </div>

      {selectedParent && childTags.length > 0 && (
        <div className="u-col u-w100 u-gap8">
          <span className="bodytext-13 u-text-light-gray u-pl4">
            対象プラットフォームまたはサービス
          </span>
          <TagRow>
            {childTags.map((child) => (
              <FilterChip
                key={child.name}
                size="medium"
                onClick={() => handleChildClick(child)}
              >
                {child.name}
              </FilterChip>
            ))}
          </TagRow>
        </div>
      )}

      <Input
        type="text"
        label="目的物"
        option="required"
        placeholder="（プラットフォーム）向け（メディア）「タイトル（仮）」"
        width="100%"
        maxlength={256}
        name="final_work"
        value={value}
        onChange={handleChange}
        onCompositionEnd={handleCompositionEnd}
      />
    </div>
  );
};

export default ProjectObject;
